import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { AccountHoldingWithSecurity } from "../../../api/src/positions/positions.service";
import type { SecuritiesController } from "../../../api/src/securities/securities.controller";
import type { Security } from "../../../api/src/securities/securities.service";
import { deserializeDate, useAuthenticatedQuery } from "./api";

/**
 * The console path of a security's page (APP-1486), which replaced the link
 * out to Yahoo Finance. Bonds use the CUSIP the security master stores as
 * their symbol.
 */
export function securityPath(symbol: string) {
  return `/firm/security-master/${encodeURIComponent(symbol)}`;
}

/**
 * The Security Master filtered to one security's row (APP-1486). The page reads
 * `symbol` into the filter of its Symbol column.
 */
export function securityMasterRowPath(symbol: string) {
  return `/firm/security-master?${new URLSearchParams({ symbol })}`;
}

/**
 * The Firm tab bar's "Security Category" page (`FirmNav`) with one category's
 * row marked (APP-1486). The page reads `category` and highlights that row.
 * With no category, the path is the whole page.
 */
export function categoryAssetClassPath(category?: string) {
  return category
    ? `/firm/risk/security-category?${new URLSearchParams({ category })}`
    : "/firm/risk/security-category";
}

/**
 * The Firm tab bar's "Asset Class" page (`FirmNav`) with the rows of the given
 * asset classes marked (APP-1486). The page reads each `assetClass` and
 * highlights those rows. With no asset class, the path is the whole page.
 */
export function assetClassRiskPath(assetClasses: string[]) {
  const params = new URLSearchParams(
    assetClasses.map((assetClass) => ["assetClass", assetClass]),
  );
  const query = params.toString();
  return `/firm/risk/asset-class${query ? `?${query}` : ""}`;
}

export const CASH_SYMBOLS = [
  "CASH-1",
  "FCASH",
  "FTEXX",
  "FNSXX",
  "SPAXX",
  "FDRXX",
  "FDUXX",
  "SPRXX",
];

/**
 * The decimal places of a `riskAllocation` value on 0 to 1: the API sends 6
 * places on 0 to 100 (`api/src/securities/securities.controller.ts`,
 * `RISK_ALLOCATION_PERCENT_DECIMALS`), so 8 on 0 to 1.
 */
const RISK_ALLOCATION_RATIO_DECIMALS = 8;

/**
 * `POST /v1/securities` sends each `riskAllocation` value on 0 to 100
 * (`an-decision-01a0ef4b7e9377c9` row 1). Dividing by 100 here keeps every
 * console consumer on the 0 to 1 ratio it computes with
 * (`client/src/clients/account/AccountHoldingsTable.tsx`). The quotient is
 * rounded to {@link RISK_ALLOCATION_RATIO_DECIMALS}, so `7.006652` comes back
 * as `0.07006652` and not as a neighbouring double.
 */
export function deserializeSecurity(
  security: SerializedObject<Security>,
): Security {
  return {
    ...security,
    riskAllocation: Object.fromEntries(
      Object.entries(security.riskAllocation).map(([riskName, allocation]) => [
        riskName,
        Math.round(allocation * 10 ** (RISK_ALLOCATION_RATIO_DECIMALS - 2)) /
          10 ** RISK_ALLOCATION_RATIO_DECIMALS,
      ]),
    ),
    createdTime: deserializeDate(security.createdTime),
    updatedTime: deserializeDate(security.updatedTime),
  };
}

/**
 * Calculate the total value of cash positions from holdings.
 * Uses known cash symbols to identify cash holdings.
 *
 * @param holdings Array of account holdings with security information
 * @returns Total value of cash positions
 */
export function calculatePositionsCash(
  holdings: AccountHoldingWithSecurity[],
): number {
  return holdings
    .filter((holding) => {
      const security = holding.security;
      return security && CASH_SYMBOLS.includes(security.symbol ?? "");
    })
    .reduce((sum, holding) => sum + holding.value, 0);
}

/**
 * Get all security records for the given symbols.
 *
 * A `""` symbol is left out of the request, and no request is sent when no
 * other symbol remains: `POST /v1/securities` answers 400 when `symbols` holds
 * an empty string (`GetMultipleSecuritiesDto`), and a row newly added to a
 * form (`PrimarySecurities.tsx`, `SecurityGroupSecondarySecurities.tsx`,
 * `SecurityRestrictions.tsx`) has `""` for its symbol.
 * @param symbols The collection of security symbols to fetch
 * @param param1 The optional query configuration
 * @returns A query result with a collection of security records
 */

export function useQuerySecurities(symbols: string[], { enabled = true } = {}) {
  const sentSymbols = symbols.filter((symbol) => symbol !== "");

  return useAuthenticatedQuery<
    UnpackResponse<SecuritiesController["getMultipleSecurities"]>,
    Security[],
    [string, string, string[]]
  >({
    queryKey: ["v1", "securities", sentSymbols],
    queryFn: async (securities) => securities.data.map(deserializeSecurity),
    urlBuilderFn: (key) => `/${key[0]}/${key[1]}`,
    fetchOptions: {
      method: "POST",
      body: { symbols: sentSymbols },
    },
    queryOptions: { enabled: enabled && sentSymbols.length > 0 },
  });
}
